import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, toast, useCopy } from "@sagot/ui";
import type { CustomDomainOut } from "../../shared";
import { TableScroll } from "./panel-kit";

type DnsRecord = CustomDomainOut["required_records"][number];

const CNAME = "CNAME";

function CopyValue({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  const onCopy = async () => {
    if (await copy(value)) toast.success(t("common.copied"));
    else toast.error(t("settings.copyFailed"));
  };
  return (
    <span className="flex items-center gap-1.5">
      <span className="min-w-0 break-all font-mono text-xs">{value}</span>
      <Button variant="ghost" size="sm" iconOnly aria-label={label} className="size-8 shrink-0" onClick={() => void onCopy()}>
        <Icon name={copied ? "check" : "copy"} />
      </Button>
    </span>
  );
}

/**
 * The DNS records the hosting provider asked for, kept on the domain so they are still here after a
 * reload (BUG-DOMAINS-001; mockup domains pin 1). Name is relative to the zone; the CNAME must be
 * "DNS only" (grey cloud) on Cloudflare.
 */
export function DomainRecordsTable({ domain, records }: { domain: CustomDomainOut; records: readonly DnsRecord[] }) {
  const { t } = useTranslation();
  const found = domain.progress.dns.state === "done";
  return (
    <div data-testid="domain-records">
      <p className="text-sm font-semibold">{t("settings.domains.recordsTitle", { count: records.length })}</p>
      <TableScroll className="mt-3" label={t("settings.domains.recordsLabel", { host: domain.hostname })}>
        <table className="table min-w-[640px]">
          <thead>
            <tr>
              <th>{t("settings.domains.colType")}</th>
              <th>{t("settings.domains.colName")}</th>
              <th>{t("settings.domains.colValue")}</th>
              <th>{t("settings.domains.colProxy")}</th>
              <th>{t("settings.domains.colStatus")}</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={`${record.type}-${record.name}`} data-testid="domain-record">
                <td className="font-mono text-xs font-semibold">{record.type}</td>
                <td>
                  <CopyValue value={record.name} label={t("settings.domains.copyName", { type: record.type })} />
                </td>
                <td>
                  <CopyValue value={record.value} label={t("settings.domains.copyValue", { type: record.type })} />
                </td>
                <td className="text-xs">{record.type === CNAME ? t("settings.domains.proxyDnsOnly") : t("settings.domains.proxyNone")}</td>
                <td>
                  <Badge tone={found ? "success" : "danger"}>
                    <Icon name={found ? "check-circle" : "x-circle"} className="size-3" />
                    {found ? t("settings.domains.recordFound") : t("settings.domains.recordMissing")}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </div>
  );
}
